import { useEffect, useState } from 'react';
import { useParams, useNavigate, useOutletContext, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Loader2, ArrowLeft, Plus, Trash2, Save, CheckCircle2, Sparkles,
  MessageCircleQuestion, AlertCircle, FileText, BarChart3, ChevronRight, ChevronLeft, Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import {
  getSurveyWithAnchors,
  upsertSurvey,
  upsertAnchors,
  setActiveSurvey,
  getSurveyReadiness,
  suggestAnchors,
  generateInterviewFrameworks,
  type AdminAnchor,
} from '@/lib/admin/surveys-api';
import { DemoDataPanel } from '@/components/admin/DemoDataPanel';
import { usePlatformAdmin } from '@/lib/platform/usePlatformAdmin';

type LocalAnchor = AdminAnchor & { _local_id: string; id?: string; interview_guidance?: unknown };
const makeLocalId = () => `local-${Math.random().toString(36).slice(2, 10)}`;
const hasGuidance = (g: unknown) => !!g && typeof g === 'object' && Object.keys(g as object).length > 0;
const MAX_ANCHORS = 5;
const anchorKey = (name: string) => name.trim().toLowerCase().replace(/\s+/g, ' ');
const dedupeAnchors = (items: LocalAnchor[]) => {
  const seen = new Set<string>();
  return items.filter((a) => {
    const key = anchorKey(a.name);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, MAX_ANCHORS);
};

const STEPS = [
  { key: 'basics', label: 'Basics' },
  { key: 'vision', label: 'Vision' },
  { key: 'anchors', label: 'Anchors' },
  { key: 'frameworks', label: 'Frameworks' },
  { key: 'review', label: 'Review & activate' },
] as const;
type StepKey = typeof STEPS[number]['key'];

export default function AdminSurveyEditor() {
  const { surveyId } = useParams<{ surveyId: string }>();
  const { orgId } = useOutletContext<{ orgId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const isPlatformAdmin = usePlatformAdmin();

  const [step, setStep] = useState<StepKey>('basics');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [northVision, setNorthVision] = useState('');
  const [currentState, setCurrentState] = useState('');
  const [anchors, setAnchors] = useState<LocalAnchor[]>([]);
  const [hydrated, setHydrated] = useState(false);

  const q = useQuery({
    queryKey: ['admin', 'survey', surveyId],
    queryFn: () => getSurveyWithAnchors(surveyId!),
    enabled: !!surveyId,
  });

  const readinessQ = useQuery({
    queryKey: ['admin', 'survey-readiness', surveyId],
    queryFn: () => getSurveyReadiness(surveyId!),
    enabled: !!surveyId,
  });

  useEffect(() => {
    if (q.data && !hydrated) {
      setName(q.data.survey.name ?? '');
      setDescription(q.data.survey.description ?? '');
      setNorthVision(q.data.survey.north_vision ?? '');
      const fromServer = (q.data.anchors ?? []).map((a) => ({
        _local_id: makeLocalId(),
        id: a.id,
        name: a.name,
        description: a.description ?? '',
        scale_type: a.scale_type ?? 'likert_5',
        north_vision_target: a.north_vision_target ?? 4,
        sort_order: a.sort_order ?? 0,
        is_active: a.is_active ?? true,
        interview_guidance: (a as { interview_guidance?: unknown }).interview_guidance ?? null,
      }));
      setAnchors(dedupeAnchors(fromServer));
      setHydrated(true);
    }
  }, [q.data, hydrated]);

  const saveMut = useMutation({
    mutationFn: async () => {
      await upsertSurvey({ pulseOrgId: orgId, surveyId: surveyId!, name, description, northVision });
      const cleanAnchors = dedupeAnchors(anchors);
      const payload: AdminAnchor[] = cleanAnchors.map((a, idx) => ({
        id: a.id,
        name: a.name,
        description: a.description ?? null,
        scale_type: a.scale_type ?? 'likert_5',
        north_vision_target: a.north_vision_target ?? null,
        sort_order: idx + 1,
        is_active: a.is_active ?? true,
      }));
      const upsertedIds = payload.length > 0 ? await upsertAnchors(surveyId!, payload) : [];
      return { cleanAnchors, upsertedIds };
    },
    onSuccess: ({ cleanAnchors, upsertedIds }) => {
      setAnchors(cleanAnchors.map((a, idx) => ({ ...a, id: a.id ?? upsertedIds[idx] })));
      qc.invalidateQueries({ queryKey: ['admin', 'survey', surveyId] });
      qc.invalidateQueries({ queryKey: ['admin', 'surveys', orgId] });
      qc.invalidateQueries({ queryKey: ['admin', 'survey-readiness', surveyId] });
      // NOTE: do NOT setHydrated(false) here. Local state already matches what
      // was just saved; re-hydrating from the refetch would clobber any
      // keystrokes the admin makes while the background query resolves
      // (the "title/vision/anchors disappear" bug).
      toast({ title: 'Saved' });
    },
    onError: (e: Error) => toast({ title: 'Save failed', description: e.message, variant: 'destructive' }),
  });

  const activateMut = useMutation({
    mutationFn: () => setActiveSurvey(orgId, surveyId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'surveys', orgId] });
      toast({ title: 'Activated', description: 'Charles will use this survey on the next call.' });
    },
    onError: (e: Error) =>
      toast({ title: 'Activation failed', description: e.message, variant: 'destructive' }),
  });

  const suggestMut = useMutation({
    mutationFn: async () => {
      if (!northVision.trim()) throw new Error('Add a vision first.');
      if (!currentState.trim()) throw new Error('Describe current reality first.');
      return suggestAnchors({ surveyId: surveyId!, northVision, currentState });
    },
    onSuccess: (suggested) => {
      setAnchors((cur) => {
        const existing = new Set(cur.map((a) => anchorKey(a.name)).filter(Boolean));
        const nextSuggestions = suggested
          .filter((a) => !existing.has(anchorKey(a.name)))
          .map((a, i) => ({
          _local_id: makeLocalId(),
          name: a.name,
          description: a.description ?? '',
          scale_type: 'likert_5' as const,
          north_vision_target: a.north_vision_target ?? 4,
          sort_order: cur.length + i + 1,
          is_active: true,
        }));
        return dedupeAnchors([...cur, ...nextSuggestions]);
      });
      toast({ title: 'Suggested anchors updated', description: 'Duplicates were ignored. Review and Save.' });
    },
    onError: (e: Error) => toast({ title: 'AI suggest failed', description: e.message, variant: 'destructive' }),
  });

  const frameworkMut = useMutation({
    mutationFn: (regenerate: boolean) => generateInterviewFrameworks({ surveyId: surveyId!, regenerate }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['admin', 'survey', surveyId] });
      qc.invalidateQueries({ queryKey: ['admin', 'survey-readiness', surveyId] });
      setHydrated(false);
      const failedSuffix = res.failed > 0 ? ` — ${res.failed} failed, try again.` : '';
      toast({
        title: `Generated ${res.count} of ${res.attempted} framework${res.attempted === 1 ? '' : 's'}`,
        description: (res.warning ?? 'Anchors are ready for activation.') + failedSuffix,
        variant: res.failed > 0 ? 'destructive' : 'default',
      });
    },
    onError: (e: Error) =>
      toast({ title: 'Framework gen failed', description: e.message, variant: 'destructive' }),
  });

  if (q.isLoading) return <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />;
  if (q.error) return <p className="text-sm text-destructive">{(q.error as Error).message}</p>;

  const addAnchor = () =>
    setAnchors((cur) => [
      ...cur,
      { _local_id: makeLocalId(), name: '', description: '', scale_type: 'likert_5', north_vision_target: 4, sort_order: cur.length + 1, is_active: true },
    ]);
  const updateAnchor = (lid: string, patch: Partial<LocalAnchor>) =>
    setAnchors((cur) => cur.map((a) => (a._local_id === lid ? { ...a, ...patch } : a)));
  const removeAnchor = (lid: string) => setAnchors((cur) => cur.filter((a) => a._local_id !== lid));

  const readiness = readinessQ.data;
  const ready = readiness?.ready ?? false;
  const anyMissingGuidance = anchors.some((a) => a.id && !hasGuidance(a.interview_guidance));
  const hasUnsavedAnchors = anchors.some((a) => !a.id);

  const stepIndex = STEPS.findIndex((s) => s.key === step);
  const goPrev = () => stepIndex > 0 && setStep(STEPS[stepIndex - 1].key);
  const goNext = () => stepIndex < STEPS.length - 1 && setStep(STEPS[stepIndex + 1].key);

  // step gating
  const canLeaveBasics = name.trim().length > 0;
  const canLeaveVision = northVision.trim().length > 0;
  const canLeaveAnchors = anchors.length > 0;
  const stepValid: Record<StepKey, boolean> = {
    basics: canLeaveBasics,
    vision: canLeaveVision,
    anchors: canLeaveAnchors,
    frameworks: !anyMissingGuidance,
    review: ready,
  };

  // Human-readable "why this step isn't done yet" — used on chips + Next btn
  const stepBlockedReason: Record<StepKey, string | null> = {
    basics: canLeaveBasics ? null : 'Add a survey name',
    vision: canLeaveVision ? null : 'Write a North-Star vision',
    anchors: canLeaveAnchors ? null : 'Add at least one anchor',
    frameworks: anyMissingGuidance
      ? 'Generate interview frameworks for every active anchor'
      : hasUnsavedAnchors
      ? 'Save new anchors before generating frameworks'
      : null,
    review: ready ? null : 'Finish previous steps to activate',
  };

  // Sequential prerequisite check — true if every earlier step is valid
  const prereqMet = (idx: number) =>
    STEPS.slice(0, idx).every((s) => stepValid[s.key]);

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/app/admin/surveys')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <div className="flex gap-2">
          <Button asChild variant="ghost" size="sm">
            <Link to={`/app/admin/surveys/${surveyId}/report`}>
              <FileText className="h-4 w-4 mr-2" /> Report
            </Link>
          </Button>
          <Button asChild variant="ghost" size="sm">
            <Link to={`/app/admin/surveys/${surveyId}/analytics`}>
              <BarChart3 className="h-4 w-4 mr-2" /> Analytics
            </Link>
          </Button>
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
            {saveMut.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save
          </Button>
        </div>
      </div>

      {/* Stepper — numbered, status-aware, sequential */}
      <nav aria-label="Wizard steps" className="flex items-center gap-1.5 flex-wrap">
        {STEPS.map((s, i) => {
          const active = s.key === step;
          const done = stepValid[s.key] && !active;
          const locked = !prereqMet(i) && !done && !active;
          const titleText = locked
            ? `Complete step ${i} first`
            : stepBlockedReason[s.key] ?? 'Done';
          return (
            <div key={s.key} className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setStep(s.key)}
                title={titleText}
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                  active
                    ? 'bg-primary text-primary-foreground border-primary'
                    : done
                    ? 'bg-muted text-foreground border-border'
                    : locked
                    ? 'bg-background text-muted-foreground/70 border-dashed border-border hover:text-foreground'
                    : 'bg-background text-muted-foreground border-border hover:text-foreground',
                )}
              >
                <span className={cn(
                  'h-5 w-5 rounded-full text-[10px] flex items-center justify-center font-semibold',
                  active
                    ? 'bg-primary-foreground/20'
                    : done
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground/70',
                )}>
                  {done ? <Check className="h-3 w-3" /> : i + 1}
                </span>
                <span>{s.label}</span>
              </button>
              {i < STEPS.length - 1 && (
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/40" />
              )}
            </div>
          );
        })}
      </nav>

      {/* Sequence hint — tells admin exactly what's next or what's blocking */}
      <div
        className={cn(
          'flex items-start gap-2 rounded-md border px-3 py-2 text-xs',
          stepBlockedReason[step]
            ? 'border-amber-500/30 bg-amber-500/5 text-foreground'
            : 'border-border bg-muted/40 text-muted-foreground',
        )}
      >
        {stepBlockedReason[step] ? (
          <>
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />
            <span>
              <span className="font-semibold">Step {stepIndex + 1} of {STEPS.length} — to continue:</span>{' '}
              {stepBlockedReason[step]}.
            </span>
          </>
        ) : (
          <>
            <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
            <span>
              <span className="font-semibold">Step {stepIndex + 1} of {STEPS.length} done.</span>{' '}
              {stepIndex < STEPS.length - 1
                ? `Next: ${STEPS[stepIndex + 1].label}.`
                : 'Ready to activate.'}{' '}
              <span className="text-muted-foreground">Remember to Save before leaving the page.</span>
            </span>
          </>
        )}
      </div>

      {/* Step body */}
      {step === 'basics' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-heading">Basic info</CardTitle>
            <CardDescription>Name this survey so admins recognise it.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="desc">Description (optional)</Label>
              <Textarea id="desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
            </div>
          </CardContent>
        </Card>
      )}

      {step === 'vision' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-heading">Vision &amp; current reality</CardTitle>
            <CardDescription>
              Vision is the north star saved to the survey. Current reality is only used to help AI suggest anchors — it is not saved.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>North vision</Label>
              <Textarea value={northVision} onChange={(e) => setNorthVision(e.target.value)} placeholder="e.g. People feel ownership over how they spend their week." rows={4} />
            </div>
            <div className="space-y-2">
              <Label>Current reality (not saved)</Label>
              <Textarea value={currentState} onChange={(e) => setCurrentState(e.target.value)} placeholder="e.g. People defer to leadership for most decisions and rarely push back." rows={4} />
            </div>
          </CardContent>
        </Card>
      )}

      {step === 'anchors' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base font-heading">Anchors</CardTitle>
              <CardDescription>The topics Charles will explore on the call.</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => suggestMut.mutate()} disabled={suggestMut.isPending}>
                {suggestMut.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Suggest with technology
              </Button>
              <Button variant="outline" size="sm" onClick={addAnchor}>
                <Plus className="h-4 w-4 mr-2" /> Add
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {anchors.length === 0 && (
              <p className="text-sm text-muted-foreground">No anchors yet. Add manually or use "Suggest with technology".</p>
            )}
            {anchors.map((a, idx) => {
              const guided = hasGuidance(a.interview_guidance);
              const isSaved = !!a.id;
              return (
                <div key={a._local_id} className="border border-border rounded-md p-3 space-y-3 bg-card">
                  <div className="flex items-start gap-3">
                    <div className="text-xs text-muted-foreground pt-2 w-6">{idx + 1}.</div>
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="Anchor name (e.g. Trust)"
                          value={a.name}
                          onChange={(e) => updateAnchor(a._local_id, { name: e.target.value })}
                        />
                        {isSaved && (
                          <Badge variant={guided ? 'default' : 'outline'} className="shrink-0">
                            {guided ? 'framework ✓' : 'no framework'}
                          </Badge>
                        )}
                      </div>
                      <Textarea
                        placeholder="Short description / what we're trying to learn"
                        value={a.description ?? ''}
                        onChange={(e) => updateAnchor(a._local_id, { description: e.target.value })}
                        rows={2}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Scale</Label>
                          <Select
                            value={a.scale_type ?? 'likert_5'}
                            onValueChange={(v) => updateAnchor(a._local_id, { scale_type: v as AdminAnchor['scale_type'] })}
                          >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="likert_5">Likert 1–5</SelectItem>
                              <SelectItem value="likert_7">Likert 1–7</SelectItem>
                              <SelectItem value="binary">Binary</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Target score</Label>
                          <Input
                            type="number"
                            min={1}
                            max={7}
                            value={a.north_vision_target ?? ''}
                            onChange={(e) =>
                              updateAnchor(a._local_id, {
                                north_vision_target: e.target.value ? Number(e.target.value) : null,
                              })
                            }
                          />
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeAnchor(a._local_id)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
            {hasUnsavedAnchors && (
              <p className="text-xs text-muted-foreground">
                Click "Save" above before moving to Frameworks — new anchors need an ID before Charles can build questions for them.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {step === 'frameworks' && (() => {
        const visibleAnchors = dedupeAnchors(anchors);
        const savedAnchors = visibleAnchors.filter((a) => a.id);
        const guidedCount = savedAnchors.filter((a) => hasGuidance(a.interview_guidance)).length;
        const missingCount = savedAnchors.length - guidedCount;
        const pct = savedAnchors.length === 0 ? 0 : Math.round((guidedCount / savedAnchors.length) * 100);

        return (
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-heading">Interview frameworks</CardTitle>
              <CardDescription>
                For each anchor, Charles needs a tailored set of questions grounded in your vision and current reality. Generate them once — you can regenerate anytime.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {visibleAnchors.length === 0 ? (
                <p className="text-sm text-muted-foreground">Add anchors in the previous step first.</p>
              ) : (
                <>
                  <div className="rounded-md border border-border bg-muted/30 p-4 space-y-3">
                    <div className="flex items-baseline justify-between gap-3">
                      <div>
                        <div className="text-2xl font-heading">
                          {guidedCount}<span className="text-muted-foreground text-base"> / {savedAnchors.length}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          anchors with a framework
                          {missingCount > 0 ? ` · ${missingCount} to generate` : ' · all done'}
                        </p>
                      </div>
                      <Badge variant={missingCount === 0 ? 'default' : 'outline'}>{pct}%</Badge>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-border overflow-hidden">
                      <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>

                  {hasUnsavedAnchors && (
                    <p className="text-xs text-amber-600 dark:text-amber-500">
                      You have unsaved anchors. Click <strong>Save</strong> at the top first — Charles can't build questions for anchors that don't have an ID yet.
                    </p>
                  )}

                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      onClick={() => frameworkMut.mutate(missingCount === 0)}
                      disabled={frameworkMut.isPending || savedAnchors.length === 0}
                      className="flex-1"
                    >
                      {frameworkMut.isPending ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating… (~30s)</>
                      ) : missingCount > 0 ? (
                        <><MessageCircleQuestion className="h-4 w-4 mr-2" /> Generate {missingCount} missing framework{missingCount === 1 ? '' : 's'}</>
                      ) : (
                        <><Sparkles className="h-4 w-4 mr-2" /> Regenerate all {savedAnchors.length} frameworks</>
                      )}
                    </Button>
                    {missingCount > 0 && (
                      <Button
                        variant="ghost"
                        onClick={() => frameworkMut.mutate(true)}
                        disabled={frameworkMut.isPending}
                      >
                        Or regenerate all
                      </Button>
                    )}
                  </div>

                  {frameworkMut.isPending && (
                    <p className="text-xs text-muted-foreground">
                      Charles is reading your vision + current reality and writing tailored questions for each anchor in parallel. This takes around 20-40 seconds.
                    </p>
                  )}

                  <ul className="divide-y divide-border border border-border rounded-md">
                    {visibleAnchors.map((a) => {
                      const guided = hasGuidance(a.interview_guidance);
                      return (
                        <li key={a._local_id} className="flex items-center justify-between px-3 py-2 text-sm">
                          <span className="truncate">{a.name || <em className="text-muted-foreground">Untitled</em>}</span>
                          {!a.id ? (
                            <Badge variant="outline">unsaved</Badge>
                          ) : guided ? (
                            <Badge variant="default">framework ✓</Badge>
                          ) : (
                            <Badge variant="outline">no framework</Badge>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {step === 'review' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              {ready ? <CheckCircle2 className="h-4 w-4 text-primary" /> : <AlertCircle className="h-4 w-4 text-muted-foreground" />}
              Launch readiness
            </CardTitle>
            <CardDescription>
              Activation is blocked until every active anchor has an interview framework.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {readiness ? (
              <div className="space-y-1">
                <div>Name set: <Badge variant={readiness.has_name ? 'default' : 'outline'}>{readiness.has_name ? 'yes' : 'no'}</Badge></div>
                <div>Vision set: <Badge variant={readiness.has_vision ? 'default' : 'outline'}>{readiness.has_vision ? 'yes' : 'optional'}</Badge></div>
                <div>Active anchors: <Badge variant={readiness.anchor_count > 0 ? 'default' : 'outline'}>{readiness.anchor_count}</Badge></div>
                <div>Anchors missing framework: <Badge variant={readiness.missing_guidance_count === 0 ? 'default' : 'destructive'}>{readiness.missing_guidance_count}</Badge></div>
              </div>
            ) : (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
            <div className="pt-2">
              <Button
                onClick={() => activateMut.mutate()}
                disabled={activateMut.isPending || !ready}
                title={!ready ? 'Survey not ready yet — see checklist' : undefined}
              >
                {activateMut.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Activate survey
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 'review' && surveyId && isPlatformAdmin === true && <DemoDataPanel surveyId={surveyId} />}

      {/* Wizard nav */}
      <div className="flex items-center justify-between pt-2">
        <Button variant="ghost" onClick={goPrev} disabled={stepIndex === 0}>
          <ChevronLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        {stepIndex < STEPS.length - 1 && (
          <Button
            onClick={goNext}
            disabled={!stepValid[step] && step !== 'frameworks'}
            title={stepBlockedReason[step] ?? `Next: ${STEPS[stepIndex + 1].label}`}
          >
            Next: {STEPS[stepIndex + 1].label} <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}
